/**
 * useOperationManifest Hook
 *
 * React Query hook for fetching and working with operation manifests.
 * Provides manifest data, settings availability, parameters, and connection requirements.
 *
 * Supports three types of operations:
 * 1. Built-in operations: Uses local manifests from src/data/manifests/
 * 2. Managed connectors (Azure API Hub): Fetches swagger spec and converts to manifest
 * 3. Service Providers (Azure built-in connectors): Fetches manifest from runtime API
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  getManifest,
  getSettingsAvailability,
  getParameters,
  getParameterGroups,
  getConnectionRequirements,
  isTriggerOperation,
  getOperationDescription,
  getDocumentationUrl,
  swaggerOperationToManifest,
  swaggerOperationToManifestByPath,
  serviceProviderOperationToManifest,
} from '@/services/operationManifestService';
import { isBuiltInOperation, getBuiltInManifest } from '@/data/manifests';
import type { OperationInfo, OperationManifest, SettingScope } from '@/types/operationManifest';
import type { NodeSettings } from '@/types/nodeSettings';
import type { ParameterInfo, ParameterGroup } from '@/types/operationParameter';
import { useConnectorSwagger } from './useConnectorSwagger';
import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import { fetchServiceProviderOperationManifest } from '@/services/logicAppsApi';
import { extractOperationGroup, isManagedApiConnector } from '@/utils/connections';

// Query cache options - manifests rarely change
const QUERY_OPTIONS = {
  staleTime: 1000 * 60 * 60 * 8, // 8 hours
  gcTime: 1000 * 60 * 60 * 8, // 8 hours
};

/**
 * Hook to fetch Service Provider operation manifest from runtime API
 */
function useServiceProviderManifest(
  operationGroup: string | null,
  operationId: string | null
) {
  const { getOttoToken } = useAuth();
  const { selectedApp, selectedProject } = useAppStore();

  return useQuery<{ manifest: OperationManifest | null; metadataError?: Error }>({
    queryKey: ['serviceProviderManifest', operationGroup, operationId],
    queryFn: async () => {
      if (!selectedApp || !selectedProject || !operationGroup || !operationId) {
        return { manifest: null };
      }

      try {
        const response = await fetchServiceProviderOperationManifest({
          projectName: selectedProject,
          appName: selectedApp.name,
          getToken: getOttoToken,
          operationGroup,
          operationId,
        });

        // Determine if this is a trigger based on the response
        const isTrigger = response.properties.operationType?.toLowerCase() === 'trigger' ||
                         !!response.properties.visibility?.toLowerCase().includes('trigger');

        // Convert to our OperationManifest format
        return { manifest: serviceProviderOperationToManifest(response.properties, isTrigger) };
      } catch (error) {
        console.warn(
          `[useServiceProviderManifest] Failed to fetch manifest for ${operationGroup}/${operationId}:`,
          error
        );
        return {
          manifest: null,
          metadataError: error instanceof Error ? error : new Error('Operation metadata could not be loaded'),
        };
      }
    },
    enabled: !!selectedApp && !!selectedProject && !!operationGroup && !!operationId,
    ...QUERY_OPTIONS,
  });
}

/**
 * Hook to fetch an operation manifest
 *
 * Following LogicAppsUX pattern (see StandardOperationManifestService.getOperationManifest):
 * 1. First check if we have a local built-in manifest
 * 2. If not built-in AND managed connector (Azure API Hub): Fetch swagger and convert
 * 3. If not built-in AND has connectorId/operationId: Fetch from runtime API
 */
export function useOperationManifest(operationInfo: OperationInfo | null) {
  // Check if we have a local built-in manifest for this operation
  const hasBuiltInManifest = useMemo(() => {
    if (!operationInfo?.operationId) return false;
    return isBuiltInOperation(operationInfo.operationId);
  }, [operationInfo?.operationId]);

  // Determine if this is a managed connector (Azure API Hub)
  const isManagedConnector = isManagedApiConnector(operationInfo?.connectorId ?? '');

  // Determine if we need to fetch from runtime API
  // This is for SERVICE PROVIDERS only (built-in connectors like Azure Tables, Service Bus)
  // Managed connectors (Azure API Hub) use swagger instead - the runtime API 404s for them
  const needsRuntimeFetch = !hasBuiltInManifest && !!operationInfo?.connectorId && !isManagedConnector;

  // Extract operation group from connector ID for runtime API fetch
  // For managed connectors: /managedApis/teams -> teams
  // For service providers: /serviceProviders/azureTables -> azuretables
  const operationGroup = useMemo(() => {
    if (!needsRuntimeFetch || !operationInfo?.connectorId) return null;
    return extractOperationGroup(operationInfo.connectorId);
  }, [needsRuntimeFetch, operationInfo?.connectorId]);

  // Fetch swagger for managed connectors
  const swaggerConnectorId = isManagedConnector ? operationInfo?.connectorId ?? null : null;
  const {
    data: swaggerResult,
    isLoading: swaggerLoading,
    error: swaggerError,
    refetch: refetchSwagger,
  } = useConnectorSwagger(swaggerConnectorId);
  const swagger = swaggerResult?.swagger ?? null;
  const swaggerParser = swaggerResult?.parser ?? null;

  // Fetch manifest from runtime API (for non-built-in operations)
  const {
    data: runtimeResult,
    isLoading: runtimeLoading,
    refetch: refetchRuntime,
  } = useServiceProviderManifest(
    needsRuntimeFetch ? operationGroup : null,
    needsRuntimeFetch ? operationInfo?.operationId ?? null : null
  );
  const runtimeManifest = runtimeResult?.manifest;

  // Normalize IDs for consistent cache keys (following LogicAppsUX pattern)
  const normalizedConnectorId = operationInfo?.connectorId?.toLowerCase();
  const normalizedOperationId = operationInfo?.operationId?.toLowerCase();

  // Check if we can use path/method fallback (for legacy tools without operationId)
  const hasPathAndMethod = !!operationInfo?.path && !!operationInfo?.method;


  const queryResult = useQuery({
    queryKey: [
      'operationManifest',
      normalizedConnectorId,
      normalizedOperationId,
      operationInfo?.path,
      operationInfo?.method,
      swagger?.info?.version,
      runtimeManifest ? 'runtime' : null,
    ],
    queryFn: () => {
      if (!operationInfo) {
        return null;
      }

      // 1. First check for built-in manifest (highest priority)
      if (hasBuiltInManifest && operationInfo.operationId) {
        const builtIn = getBuiltInManifest(operationInfo.operationId);
        if (builtIn) {
          return builtIn;
        }
      }

      // 2. If we fetched a runtime manifest, use it
      if (runtimeManifest) {
        return runtimeManifest;
      }

      // 3. If we have swagger for a managed connector, convert it to manifest
      if (swagger && operationInfo.operationId) {
        const manifest = swaggerOperationToManifest(swagger, operationInfo.operationId);
        if (manifest) {
          return manifest;
        }
        console.warn(
          `Failed to convert swagger to manifest for operation ${operationInfo.operationId}`
        );
      }

      // 3b. Fallback: If we have swagger but no operationId, try using path/method
      // This supports legacy tools that don't have operationId stored
      if (swagger && hasPathAndMethod) {
        const manifest = swaggerOperationToManifestByPath(
          swagger,
          operationInfo.path!,
          operationInfo.method!
        );
        if (manifest) {
          return manifest;
        }
        console.warn(
          `Failed to convert swagger to manifest using path ${operationInfo.path} method ${operationInfo.method}`
        );
      }

      if (isManagedConnector) {
        throw new Error('Cannot find operation for managed connector, definition may be missing or invalid');
      }
      // 4. Fall back to default manifest
      if (import.meta.env.DEV) {
        console.warn('[useOperationManifest] Falling through to default manifest - swagger was null/missing');
      }

      return getManifest(operationInfo);
    },
    enabled:
      !!operationInfo?.connectorId &&
      (!!operationInfo?.operationId || hasPathAndMethod) &&
      (!isManagedConnector || !swaggerLoading) &&
      (!needsRuntimeFetch || !runtimeLoading),
    ...QUERY_OPTIONS,
  });

  // Return query result properties explicitly along with swagger parser
  // This avoids spreading React Query internals and maintains type safety
  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading || swaggerLoading || runtimeLoading,
    error: queryResult.error,
    isFetching: queryResult.isFetching,
    swaggerParser,
    // About consumes these additively; existing parameter/settings error and fallback contracts stay intact.
    metadataError: swaggerError ?? runtimeResult?.metadataError ?? queryResult.error,
    refetchMetadata: async () => {
      if (isManagedConnector) await refetchSwagger();
      if (needsRuntimeFetch) await refetchRuntime();
      await queryResult.refetch();
    },
  };
}

/**
 * Options for useOperationManifestData hook
 */
export interface UseOperationManifestDataOptions {
  /**
   * Override scope for settings availability
   */
  scopeOverride?: SettingScope | undefined;
  /**
   * Node type (e.g., 'ApiConnectionNotification', 'ApiConnection')
   * Used to determine if notification trigger parameters should be fetched
   */
  nodeType?: string | undefined;
  /**
   * For notification triggers: the fetch path template (e.g., '/v2/Mail/OnNewEmail')
   * Used to resolve operationId from swagger when not explicitly stored.
   * Following Logic Apps UX pattern: operationId is not stored in workflow definition,
   * it's resolved dynamically from the swagger using the path.
   */
  fetchPath?: string | undefined;
  /**
   * For notification triggers: the fetch HTTP method (default: 'get')
   */
  fetchMethod?: string | undefined;
}

/**
 * Hook that provides all manifest-derived data for a node
 * This is the main hook for the node editor - it provides:
 * - The manifest itself
 * - Settings availability (which settings are supported)
 * - Parameters (input fields)
 * - Parameter groups (basic vs advanced)
 * - Connection requirements
 * - Utility flags (isTrigger, description, etc.)
 */
export function useOperationManifestData(
  operationInfo: OperationInfo | null,
  scopeOverrideOrOptions?: SettingScope | UseOperationManifestDataOptions
) {
  // Handle both old signature (scopeOverride) and new signature (options object)
  const options: UseOperationManifestDataOptions = typeof scopeOverrideOrOptions === 'object'
    ? scopeOverrideOrOptions
    : { scopeOverride: scopeOverrideOrOptions };

  const { scopeOverride, nodeType, fetchPath, fetchMethod = 'get' } = options;

  const {
    data: manifest,
    isLoading,
    error,
    isFetching,
    swaggerParser,
  } = useOperationManifest(operationInfo);

  // Derive settings availability from manifest
  const settings = useMemo<NodeSettings | null>(() => {
    if (!manifest) return null;
    return getSettingsAvailability(manifest, scopeOverride);
  }, [manifest, scopeOverride]);

  // Derive parameters from manifest or swagger
  // For notification triggers, use swagger parser with includeNotificationParameters
  const parameters = useMemo<ParameterInfo[]>(() => {
    // For ApiConnectionNotification triggers with swagger parser, get notification params
    if (nodeType === 'ApiConnectionNotification' && swaggerParser) {
      // Resolve operationId: use explicit operationId or resolve from fetch path
      // Following Logic Apps UX pattern: operationId is not stored in workflow definition,
      // it's resolved dynamically from the swagger using the path
      let resolvedOperationId = operationInfo?.operationId;

      if (!resolvedOperationId && fetchPath) {
        resolvedOperationId = swaggerParser.resolveOperationIdFromFetchPath(fetchPath, fetchMethod) ?? undefined;
      }

      if (resolvedOperationId) {
        const notificationParams = swaggerParser.getInputParameters(
          resolvedOperationId,
          { includeNotificationParameters: true }
        );
        if (notificationParams.length > 0) {
          return notificationParams;
        }
      }
    }

    if (!manifest) return [];
    return getParameters(manifest);
  }, [manifest, nodeType, swaggerParser, operationInfo?.operationId, fetchPath, fetchMethod]);

  // Derive parameter groups from manifest
  const parameterGroups = useMemo<ParameterGroup[]>(() => {
    if (!manifest) return [];
    return getParameterGroups(manifest);
  }, [manifest]);

  // Derive connection requirements from manifest
  const connectionRequirements = useMemo(() => {
    if (!manifest) return null;
    return getConnectionRequirements(manifest);
  }, [manifest]);

  // Utility flags
  const isTrigger = useMemo(() => {
    if (!manifest) return false;
    return isTriggerOperation(manifest);
  }, [manifest]);

  const description = useMemo(() => {
    if (!manifest) return '';
    return getOperationDescription(manifest);
  }, [manifest]);

  const documentationUrl = useMemo(() => {
    if (!manifest) return undefined;
    return getDocumentationUrl(manifest);
  }, [manifest]);

  return {
    // Raw manifest
    manifest,

    // Derived data
    settings,
    parameters,
    parameterGroups,
    connectionRequirements,

    // Utility flags
    isTrigger,
    description,
    documentationUrl,

    // Loading state
    isLoading,
    isFetching,
    error,

    // Swagger parser (for path parameter extraction and other swagger-based operations)
    swaggerParser,
  };
}

/**
 * Hook to get just settings availability for a manifest
 * Useful when you already have the manifest and just need settings
 */
export function useManifestSettings(
  manifest: OperationManifest | null,
  scopeOverride?: SettingScope
): NodeSettings | null {
  return useMemo(() => {
    if (!manifest) return null;
    return getSettingsAvailability(manifest, scopeOverride);
  }, [manifest, scopeOverride]);
}

/**
 * Hook to get just parameters for a manifest
 * Useful when you already have the manifest and just need parameters
 */
export function useManifestParameters(manifest: OperationManifest | null): ParameterInfo[] {
  return useMemo(() => {
    if (!manifest) return [];
    return getParameters(manifest);
  }, [manifest]);
}

/**
 * Hook to get parameter groups for a manifest
 * Useful when you already have the manifest and need grouped parameters
 */
export function useManifestParameterGroups(manifest: OperationManifest | null): ParameterGroup[] {
  return useMemo(() => {
    if (!manifest) return [];
    return getParameterGroups(manifest);
  }, [manifest]);
}
